import { faEllipsisVertical } from "@fortawesome/pro-regular-svg-icons/faEllipsisVertical";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Avatar, Box, Pagination, Rating, Stack, Typography } from '@mui/material';
import { AppGlobalClassKeys } from "app/appGlobalStyles";
import { AppImages } from 'app/appImages';
import { MprcConfirmDialog, MprcContainer, MprcIconButtonDropdown, MprcLink, MprcRequestIndicator, MprcSpinnerIndicator, MprcText } from 'components';
import { MprcFilterBox, MprcFilterCount, MprcFilterLine } from 'components/filter';
import { IconButtonCancel } from 'components/iconButtons';
import { IconSort } from 'components/icons';
import { MprcDropdownItems } from 'components/MprcIconButtonDropdown';
import { ExtractFieldErrors, MprcRequestErrorReport } from 'components/MprcRequestErrorReport';
import { MprcTextFieldColor } from 'components/MprcTextFieldColor';
import { MprcTypographyOnDemand } from 'components/MprcTypographyOnDemand';
import { TrainingRatingItemResponse, TrainingRatingsOrderBy, TrainingRatingsResponse } from 'generated/CoreApi_v1';
import { Formaters } from 'lib/Formaters';
import { enqueueSnackbar } from 'notistack';
import { useCoreApi } from 'providers/CoreApiProvider';
import { useModalUserProfile } from 'providers/UserProfileProvider';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

////////////////////////////////////////////////////////////////////////////

export interface ITrainingRatingViewProps {
    trainingId: string;
    classroomId?: string;
}

interface TrainingRatingFilterOptions {
    searchText: string;
    orderBy: TrainingRatingsOrderBy;
}

type RatingMenuOptions = "delete";

////////////////////////////////////////////////////////////////////////////

export const TrainingRatingView: React.FC<ITrainingRatingViewProps> = (props) => {

    const { trainingId, classroomId } = props;

    const api = useCoreApi();
    const { t } = useTranslation("lms");
    const { showUser } = useModalUserProfile();


    const [viewError, setViewError] = useState<any>();
    const [loading, setLoading] = useState<boolean>(true);
    const [dialog, setDialog] = useState<any>();
    const [deletingId, setDeleteId] = useState<string | null>(null);
    const [viewData, setViewData] = useState<TrainingRatingsResponse>({
        ratingAvg: 0,
        data: {
            items: [],
            pageNumber: 1,
            pageSize: 1,
            totalPages: 0,
            totalRows: 0
        }
    });
    const [filterOptions, setFilterOptions] = useState<TrainingRatingFilterOptions>({
        searchText: "",
        orderBy: TrainingRatingsOrderBy.MostRecent,
    });

    const pageNumber = useRef<number>(1);
    const searchRef = useRef<HTMLInputElement | undefined>(undefined);

    ////////////////////////////////////////////////////////////////////////////

    ///
    const loadPage = (page: number) => {
        pageNumber.current = page;
        setLoading(true);
    };

    ///
    const handleFilterChange = (filter: TrainingRatingFilterOptions, canReloadPage: boolean = true) => {
        setFilterOptions(filter);
        if (canReloadPage) loadPage(1);
    };

    ///
    const getOrderItems = useMemo(() => {
        const items = [
            { text: t("Student.TrainingRatingView.MostRecentText"), value: TrainingRatingsOrderBy.MostRecent, key: 0 },
            { text: t("Student.TrainingRatingView.HighestRatingText"), value: TrainingRatingsOrderBy.HighestRating, key: 1 },
            { text: t("Student.TrainingRatingView.LowestRatingText"), value: TrainingRatingsOrderBy.LowestRating, key: 2 }
        ];

        return items;
    }, [t]);

    ///
    const getCurrentOrderBy = useCallback(() => {
        var item = getOrderItems.find(x => x.value === filterOptions.orderBy);
        return item?.text;
    }, [getOrderItems, filterOptions]);


    ///
    const getMenuItems = (item: TrainingRatingItemResponse): MprcDropdownItems<RatingMenuOptions> => {

        const items: MprcDropdownItems<RatingMenuOptions> = [];

        if (item.canDelete) {
            items.push({ text: <MprcText color="error">{t("Actions.Delete", { ns: "shared" })}</MprcText>, value: "delete" });
        }

        return items;
    };

    ///
    const handleMenuOption = (option: RatingMenuOptions, item: TrainingRatingItemResponse) => {
        switch (option) {
            case "delete":
                setDialog(
                    <MprcConfirmDialog
                        open
                        severity="danger"
                        message={<span>{t("Student.TrainingRatingView.ConfirmDeleteMessage")}</span>}
                        onClose={(result) => {
                            if (result) {
                                setDeleteId(item.enrollmentId);
                            }

                            setDialog(null);
                        }
                        }
                    />
                );
                break;
        }
    };

    ////////////////////////////////////////////////////////////////////////////
    /// EFFECT'S
    ////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        async function postData() {
            try {
                if (deletingId) {
                    const response = await api.v1.enrollment.removeRating({ enrollmentId: deletingId });

                    if (response.data === true) {
                        enqueueSnackbar(t("Student.TrainingRatingView.DeleteSuccessMessage"), { variant: "success" });
                        if (viewData.data.items.length == 1 && pageNumber.current > 1) loadPage(pageNumber.current - 1);
                        else loadPage(1);
                    } else {
                        enqueueSnackbar(t("Student.TrainingRatingView.DeleteErrorMessage"), { variant: "error" });
                    }
                }
            } catch (e) {
                var errors = ExtractFieldErrors(e);
                errors.forEach(m => enqueueSnackbar(m, { variant: "error" }));
            }
            setDeleteId(null);
        }

        postData();
    }, [deletingId]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        async function getData() {
            try {
                const response = await api.v1.lmsTraining.getRatings({
                    TrainingId: trainingId,
                    ClassroomId: classroomId,
                    OrderBy: filterOptions.orderBy,
                    SearchText: filterOptions.searchText,
                    PageNumber: pageNumber.current,
                    PageSize: 10
                });
                setViewData(response.data);

                setTimeout(() => {
                    if (filterOptions.searchText.length > 0) {
                        searchRef.current?.focus();
                    }
                }, 300);

            } catch (e) {
                setViewError(e);
            }
            finally {
                setLoading(false);
            }
        }

        if (loading) getData();
    }, [loading]);

    return (
        <>
            {dialog}

            <MprcRequestIndicator visible={deletingId != null} type="post" />

            <MprcRequestErrorReport error={viewError} />

            <MprcContainer>
                <MprcFilterBox>
                    <MprcFilterLine>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Rating value={viewData.ratingAvg} readOnly precision={0.5} />
                            {viewData.data.totalRows > 1 &&
                                <Typography variant="h4" color="black" component="span">{t("Student.TrainingRatingView.AverageText")} {viewData.ratingAvg}</Typography>
                            }
                        </Stack>
                        <MprcIconButtonDropdown
                            disabled={loading}
                            text={getCurrentOrderBy()}
                            icon={<IconSort />}
                            title={t("shared:Filter.OrderBy")}
                            onChange={(orderBy) => handleFilterChange({ ...filterOptions, orderBy })}
                            items={getOrderItems}
                            selected={filterOptions.orderBy}
                            menuItemCheck={true}
                            color="primary"
                        />
                    </MprcFilterLine>
                    <MprcTextFieldColor
                        inputRef={searchRef}
                        fullWidth
                        disabled={loading}
                        value={filterOptions.searchText}
                        onChange={e => handleFilterChange({ ...filterOptions, searchText: e.target.value }, false)}
                        placeholder={t("Student.TrainingRatingView.SearchPlaceholder")}
                        onKeyDown={(e) => { if (e.keyCode == 13) loadPage(1); }}
                        endAdornment={
                            !filterOptions.searchText
                                ? null
                                : <IconButtonCancel size="small" onClick={() => handleFilterChange({ ...filterOptions, searchText: "" })} />
                        }
                    />
                </MprcFilterBox>
            </MprcContainer>

            <Box sx={{ maxHeight: "50vh", overflow: "auto" }} className={AppGlobalClassKeys.thinScrollbar}>
                {loading ?
                    <MprcSpinnerIndicator /> :

                    <>
                        {viewData.data.items.length === 0 ?
                            <MprcContainer>
                                <MprcFilterCount
                                    loading={loading}
                                    isEmpty={viewData.data.totalRows == 0}
                                    countText={t("Student.TrainingRatingView.RecordsFound", { count: viewData.data.totalRows })}
                                    filter={{
                                        active: filterOptions.searchText.length > 0,
                                        text: t("shared:Filter.FilteredRecords", { count: viewData.data.totalRows }),
                                        onResetFilter: () => handleFilterChange({ ...filterOptions, searchText: "" })
                                    }}
                                />
                            </MprcContainer>
                            :
                            viewData.data.items.map(item =>
                                <MprcContainer>
                                    <Stack direction="row" alignItems="flex-end" justifyContent="space-between">
                                        <Stack direction="row" spacing={1} justifyItems="center" alignItems="center">
                                            <Rating value={item.ratingStars} readOnly />
                                            <div>
                                                <Typography variant="body2" fontWeight="bold" component="span">{item.name}</Typography>
                                                <Typography variant="body2" component="span">
                                                    <>, </>
                                                    {Formaters.relativeTime(item.publishedOn)}
                                                </Typography>
                                            </div>
                                            {getMenuItems(item).length > 0 &&
                                                <MprcIconButtonDropdown
                                                    buttonSize="small"
                                                    icon={<FontAwesomeIcon icon={faEllipsisVertical} />}
                                                    onChange={(selectedValue) => handleMenuOption(selectedValue, item)}
                                                    items={getMenuItems(item)}
                                                />
                                            }

                                        </Stack>
                                        <Stack direction="row" spacing={1}>
                                            <MprcLink
                                                onClick={() => showUser(item.userId)}
                                                title={item.name}
                                            >
                                                <Avatar
                                                    variant="circular"
                                                    sx={{ width: 35, height: 35 }}
                                                    src={item.thumbnailUrl ?? AppImages.UserThumbnail}

                                                />
                                            </MprcLink>
                                        </Stack>
                                    </Stack>
                                    <MprcTypographyOnDemand maxRows={4}>{item.ratingComment}</MprcTypographyOnDemand>
                                </MprcContainer>
                            )}
                    </>
                }
            </Box>
            {
                viewData && viewData.data.totalPages > 1 &&
                <Pagination
                    shape="rounded"
                    color="primary"
                    page={viewData.data.pageNumber}
                    count={viewData.data.totalPages}
                    onChange={(e, pg) => loadPage(pg)}
                />
            }
        </>
    );
};
